// Description: Page to finish the sign up process
// Updated: 01/10/2025
// Author: Simon Franklin

// React
import * as React from 'react';
import { useEffect, useState } from 'react';

// Material UI Components
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

// Components and Functions for the project
import AddressForm from './components/AddressForm';
import { useNavigate } from 'react-router-dom';
import PersonalInformation from './components/PersonalInformation';
import { insertMaskForPhoneNumber, notify } from '../../utils/utils';
import themeColors from '../../components/ThemeColors/ThemeColors';
import InfoMobile from './components/InfoMobile';
import Info from './components/Info.jsx';
import { FinishRegister } from '../../api/aws/Profile/UpdateProfileInfo.js';
import DynamicButton from '../../components/DynamicButton/DynamicButton.js';
import { Refresh } from '../../api/aws/Refresh/RefreshToken.js';
import use123zapStore from '../../context/123zapStore.js';

// SitemarkIcon
export function SitemarkIcon() {
    return (
        <svg width="118" height="37" viewBox="0 0 118 37" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1895_365)">
                <path d="M39.1513 0V30.8242H11.6581C8.78254 30.8242 6.41891 28.5375 6.41891 25.6669V7.484H6.26105L0 0H39.1513Z" fill="#D4BDFF" />
                <path d="M44.548 0V30.8242H13.4305V27.645C13.4305 22.5276 16.4234 20.7427 22.4498 16.1691C25.6412 13.8047 27.0192 12.4082 27.0192 10.3146C27.0192 8.56964 25.799 7.3685 23.7895 7.3685C21.6627 7.3685 19.89 9.15131 19.0239 11.2071L12.3682 8.26095C13.55 4.57565 17.409 0.155392 24.0263 0H44.548Z" fill="#A789EF" />
                <path d="M52.819 7.3286V23.9723C52.819 27.7269 49.7173 30.7738 45.903 30.7906C43.7698 30.799 42.4323 30.8158 41.5939 30.7864C41.3187 30.8242 41.0435 30.8242 40.7662 30.8242C34.5819 30.8242 30.3667 27.0234 28.9502 23.3801L35.3307 20.0077C36.3547 21.9858 37.9311 23.5355 40.5294 23.5355C42.4984 23.5355 43.7207 22.4499 43.7207 20.9758C43.7207 19.2707 42.4216 18.1451 40.4121 18.1451H36.0005V12.4061H40.6489C42.1848 12.4061 43.5245 11.205 43.5245 9.80857C43.5245 8.02577 42.0675 7.17111 40.5699 7.17111C38.3258 7.17111 36.6704 8.52764 35.5291 10.6212L29.4643 7.59739C31.156 3.14143 35.608 0 40.9646 0C41.2398 0 41.5171 0 41.7517 0.037798V0H45.3761C49.4698 0 52.8211 3.29682 52.8211 7.3286H52.819Z" fill="#7428EA" />
                <path d="M58.4232 26.3032L70.077 14.4325H60.7484L61.3052 11.2512H74.96L74.4032 14.3758L62.7686 26.2465H72.8844L72.3084 29.4278H57.8643L58.421 26.3032H58.4232Z" fill="#7428EA" />
                <path d="M75.141 20.245C76.1415 14.6593 80.8346 10.8732 86.0845 10.8732C89.2203 10.8732 91.0848 12.0093 92.1429 13.7312L92.5844 11.2512H96.3923L93.1412 29.4257H89.3334L89.775 26.9457C88.1025 28.6677 85.8136 29.8037 82.6777 29.8037C77.4278 29.8037 74.1384 25.8286 75.1388 20.2429L75.141 20.245ZM83.7571 26.4355C87.3154 26.4355 90.3531 23.5964 90.9696 20.245C91.5648 16.8936 89.4699 14.2435 85.9309 14.2435C82.3918 14.2435 79.5461 16.8936 78.9488 20.245C78.3344 23.5964 80.1989 26.4355 83.7571 26.4355Z" fill="#7428EA" />
                <path d="M95.1855 37L99.8019 11.2533H103.61L103.168 13.7333C104.841 12.0114 107.13 10.8753 110.265 10.8753C115.515 10.8753 118.824 14.8504 117.823 20.4361C116.823 26.0218 112.111 29.8079 106.861 29.8079C103.725 29.8079 101.86 28.6719 100.802 26.9499L98.9955 37.0021H95.1877L95.1855 37ZM107.014 26.4355C110.573 26.4355 113.418 23.7854 114.016 20.434C114.613 17.0826 112.746 14.2435 109.188 14.2435C105.63 14.2435 102.592 17.0826 101.995 20.434C101.4 23.7854 103.475 26.4355 107.014 26.4355Z" fill="#7428EA" />
            </g>
            <defs>
                <clipPath id="clip0_1895_365">
                    <rect width="118" height="37" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
}

// Render The component based on the step
function getStepContent(step, setCanAdvance, setCheckoutData, checkoutData, handleFinish) {
    switch (step) {
        case 0:
            return <PersonalInformation setCheckoutData={setCheckoutData} checkoutData={checkoutData} />;
        case 1:
            return <AddressForm setCheckoutData={setCheckoutData} checkoutData={checkoutData} />;
        // case 2:
        //     return <PaymentForm setCheckoutData={setCheckoutData} checkoutData={checkoutData} />;
        case 2:
            return (<Stack spacing={2} useFlexGap>
                <Typography variant="h5">Finalizando seu Cadastro</Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Agora que preencheu o formulário clique no botão abaixo para concluir o processo de cadastro.
                </Typography>
                <DynamicButton
                    variant="contained"
                    style={{ alignSelf: 'start', width: { xs: '100%', sm: 'auto' } }}
                    text={"Finalizar Cadastro"}
                    loadingText={"Aguarde..."}
                    handleFunction={async () => await handleFinish()}
                />
            </Stack>)
        default:
            throw new Error('Unknown step');
    }
}

// Render The Page
export default function FinishSignUp() {
    const [checkoutData, setCheckoutData] = useState({
        person_type: 'PF',
        cellular: '',
        cpf_cnpj: '',
        firstName: '',
        lastName: '',
        telephone: '',
        email: '',
        email_cc: '',
        neighborhood: '',
        street: '',
        streetNumber: '',
        complement: '',
        city: '',
        state: '',
        zipcode: '',
        country: '',
        saveAddress: false,
        start_date: '',
        end_date: '',
        created_at: '',
        updated_at: '',
        // paymentType: 'creditCard',
        // number_card: '',
        // name_card: '',
        // security_code: '',
        // expirationDate: '',
        // saveCard: false,
        // number: '',
        // extraFunctionalities: ''
    });

    const [activeStep, setActiveStep] = useState(0);
    const selected_plan = JSON.parse(localStorage.getItem("selected_plan")) || null;

    const steps = ['Informações Pessoais', 'Endereço', /**selected_plan?.name !== 'Plano Empresarial' ? 'Método de pagamento' : 'Suas necessidades' ,*/ /*'Revisão' */ 'Finalizar'];
    const [canAdvance, setCanAdvance] = useState(false);
    const navigate = useNavigate();
    const noPaymentOptions = ['Plano FreeZap'];
    const setFinishedRegister = use123zapStore((state) => state.setFinishedRegister);

    function getZapCode(name) {
        const zapMap = {
            FreeZap: 1,
            StartZap: 2,
            ProZap: 3,
            UltraZap: 4,
        };

        return zapMap[name] || null; // retorna null se o nome não for encontrado
    }

    const validateStep = (step, data) => {
        if (step === 0) {
            let personalFields = [];
            if (checkoutData.person_type === 2) {
                personalFields = [
                    data.person_type,
                    data.firstName,
                    data.telephone,
                    data.cellular,
                    data.email,
                    data.email_cc,
                ];
            } else {
                personalFields = [
                    data.person_type,
                    data.firstName,
                    data.lastName,
                    data.telephone,
                    data.cellular,
                    data.email,
                    data.email_cc,
                ];
            }
            return personalFields.every((field) => field !== '' && field !== null);
        }

        if (step === 1) {
            const addressFields = [
                data.street,
                data.neighborhood,
                data.city,
                data.state,
                data.zipcode,
                data.country,
                data.saveAddress,
            ];
            return addressFields.every((field) => field !== '' && field !== null && field !== false);
        }

        if (step === 2) {
            let paymentFields = [];
            if (selected_plan?.name === 'Plano Empresarial') {
                paymentFields = [data.extraFunctionalities];
            } else {
                paymentFields = [
                    data.number_card,
                    data.name_card,
                    data.security_code,
                    data.expirationDate,
                    data.saveCard
                ];
            }
            return paymentFields.every((field) => field !== '' && field !== null && field !== false);
        }

        return true;
    };

    useEffect(() => {
        setCanAdvance(validateStep(activeStep, checkoutData));
    }, [checkoutData, activeStep]);

    // useEffect(() => {
    //     if (!selected_plan) navigate("/planos")
    // }, [selected_plan]);

    const handleNext = () => {
        const noPayment = noPaymentOptions.includes(String(selected_plan?.name));
        if (canAdvance) {
            setActiveStep((prev) => noPayment === true && activeStep === 1 ? 3 : prev + 1);
        } else {
            notify("Error", "Preencha todos os campos do formulário.", true);
        }
    };

    const handleBack = () => {
        const noPayment = noPaymentOptions.includes(String(selected_plan?.name));
        setActiveStep((prev) => noPayment === true && activeStep === 3 ? 1 : prev - 1);
    };

    const handleFinish = async () => {
        
        const data = {
            person_type: checkoutData.person_type ?? null,
            firstName: checkoutData.firstName ?? null,
            lastName: checkoutData.lastName ?? null,
            cpf_cnpj: checkoutData.cpf_cnpj ?? null,
            telephone: insertMaskForPhoneNumber(checkoutData.telephone) ?? null,
            cellular: insertMaskForPhoneNumber(checkoutData.cellular) ?? null,
            email: checkoutData.email ?? null,
            email_cc: checkoutData.email_cc ?? null,
            zipcode: checkoutData.zipcode ?? null,
            street: checkoutData.street ?? null,
            number: checkoutData.streetNumber ?? null,
            // complement: checkoutData.complement ?? null,
            neighborhood: checkoutData.neighborhood ?? null,
            city: checkoutData.city ?? null,
            state: checkoutData.state ?? null,
            // start_date: hoje.toISOString().split("T")[0] ?? null,
            // end_date: hoje.toISOString().split("T")[0] ?? null,
            // created_at: hoje ?? null,
            // updated_at: hoje ?? null,
            // name_card: checkoutData.name_card ?? null,
            // number_card: checkoutData.number_card ?? null,
            // expiration_month: parseMonthYear(checkoutData.expirationDate).month ?? null,
            // expiration_year: parseMonthYear(checkoutData.expirationDate).year ?? null,
            // security_code: checkoutData.security_code ?? null,
            // extraFunctionalities: checkoutData.extraFunctionalities ?? null,
            plan_id: 1,

        };

        await FinishRegister(data).then(async (response) => {
            if (response.status === 'success') {

                const rToken = localStorage.getItem("refreshToken");

                if (!rToken) throw new Error("Token não encontrado!");

                const refreshedData = await Refresh({ refresh_token: rToken });

                if (refreshedData.id_token) {
                    const authTokens = {
                        accessToken: refreshedData.access_token,
                        refreshToken: rToken,
                        idToken: refreshedData.id_token ?? "",
                        expiresAt: Date.now() + refreshedData.expires_in * 1000,
                    };

                    localStorage.setItem("expiration_time", authTokens.expiresAt);
                    localStorage.setItem("jwt_token", authTokens.accessToken);
                    localStorage.setItem("idToken", authTokens.idToken);
                    localStorage.setItem("refreshToken", authTokens.refreshToken);

                    notify("Success", "Cadastro finalizado com sucesso!", true);
                    setFinishedRegister(true);
                    navigate("/");
                }
            }
        })
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                height: 'auto',
                mt: {
                    xs: 2,
                    sm: 0,
                },
                display: "flex",
                flexDirection: "row",
                background: "#fff"
            }}
        >
            {/* <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    backgroundColor: '#fff',
                    borderRight: { sm: 'none', md: '1px solid' },
                    borderColor: { sm: 'none', md: 'divider' },
                    alignItems: 'start',
                    pt: 12,
                    px: 10,
                    gap: 4,
                    width: { xs: "100%", sm: "30%" }
                }}
            >
                <SitemarkIcon />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        width: '100%',
                        maxWidth: 500,
                    }}
                >
                    <Info selected_plan={selected_plan} />
                </Box>
            </Box> */}
            <Box
                item
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '100%',
                    width: '100%',
                    backgroundColor: { xs: 'transparent', sm: 'background.default' },
                    alignItems: 'start',
                    pt: { xs: 0, sm: 12 },
                    px: { xs: 2, sm: 10 },
                    gap: { xs: 4, md: 8 },
                    width: { xs: "100%", sm: "70%" }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: { sm: 'space-between', md: 'flex-end' },
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: { sm: '100%', md: 600 },
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            flexGrow: 1,
                        }}
                    >
                        <Stepper
                            id="desktop-stepper"
                            activeStep={activeStep}
                            sx={{ width: '100%', height: 40 }}
                        >
                            {steps.map((label) => (
                                <Step
                                    key={label}
                                >
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}

                        </Stepper>
                    </Box>
                </Box>
                <Card sx={{ display: { xs: 'flex', md: 'none' }, width: '100%' }}>
                    <CardContent
                        sx={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div>
                            <Typography variant="subtitle2" gutterBottom>
                                Produtos selecionados
                            </Typography>
                            <Typography variant="body1">
                                {activeStep >= 2 ? '$144.97' : '$134.98'}
                            </Typography>
                        </div>
                        <InfoMobile totalPrice={activeStep >= 2 ? '$144.97' : '$134.98'} />
                    </CardContent>
                </Card>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        width: '100%',
                        maxWidth: { sm: '100%', md: 600 },
                        maxHeight: { xs: 'auto', sm: '720px' },
                        gap: { xs: 5, md: 'none' },
                    }}
                >
                    <Stepper
                        id="mobile-stepper"
                        activeStep={activeStep}
                        alternativeLabel
                        sx={{ display: { sm: 'flex', md: 'none' } }}
                    >
                        {steps.map((label) => (
                            <Step
                                sx={{
                                    '& .MuiStepConnector-root': { top: { xs: 6, sm: 12 } },
                                }}
                                key={label}
                            >
                                <StepLabel
                                    sx={{ '.MuiStepLabel-labelContainer': { maxWidth: '70px' } }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    {activeStep === steps.length ? (
                        <Stack spacing={2} useFlexGap>
                            <Typography variant="h5">Finalizando seu Cadastro</Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Agora que preencheu o formulário clique no botão abaixo para concluir o processo de cadastro.
                            </Typography>
                            <DynamicButton
                                variant="contained"
                                style={{ alignSelf: 'start', width: { xs: '100%', sm: 'auto' } }}
                                text={"Finalizar Cadastro"}
                                loadingText={"Aguarde..."}
                                handleFunction={async () => await handleFinish()}
                            />
                        </Stack>
                    ) : (
                        <React.Fragment>
                            {getStepContent(activeStep, setCanAdvance, setCheckoutData, checkoutData, handleFinish)}
                            <Box
                                sx={[
                                    {
                                        display: 'flex',
                                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                                        alignItems: 'end',
                                        flexGrow: 1,
                                        gap: 1,
                                        pb: { xs: 12, sm: 0 },
                                        mt: { xs: 2, sm: 0 },
                                        mb: '60px',
                                        justifyContent: 'space-between',
                                    }
                                ]}
                            >
                                <Button
                                    startIcon={<ChevronLeftRoundedIcon />}
                                    onClick={activeStep === 0 ? () => navigate(-1) : handleBack}
                                    variant="text"
                                    sx={{ display: { xs: 'none', sm: 'flex' }, color: themeColors.purple500 }}
                                >
                                    {activeStep === 0 ? 'Voltar' : 'Anterior'}
                                </Button>
                                <Button
                                    startIcon={<ChevronLeftRoundedIcon />}
                                    onClick={activeStep === 0 ? undefined : handleBack}
                                    variant="outlined"
                                    fullWidth
                                    sx={{ display: { xs: 'flex', sm: 'none' }, color: themeColors.purple500 }}
                                >
                                    {activeStep === 0 ? 'Voltar' : 'Anterior'}
                                </Button>
                                <Button
                                    endIcon={<ChevronRightRoundedIcon color='#fff' />}
                                    variant="contained"
                                    onClick={activeStep === 3 ? handleFinish : handleNext}
                                    sx={{ width: { xs: '100%', sm: 'fit-content' }, color: '#fff', display: activeStep === steps.length - 1 ? 'none' : 'flex' }}
                                >
                                    {activeStep === steps.length - 1 ? 'Finalizar cadastro' : 'Próximo'}
                                </Button>
                            </Box>
                        </React.Fragment>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
